import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUser } from "@/contexts/UserContext";

export const ADMIN_ONLY_MESSAGE = "Only admins can apply changes.";

interface GuardValue { show: () => void }
const GuardContext = createContext<GuardValue | null>(null);

/**
 * Company settings are READ-ONLY for a member: they can look at every tab and fill in the fields, but only an admin applies changes. The red line this provider renders at
 * the top of Settings is what a member sees when they press Save in a company section (Company, AI Usage). It goes away when they change tab.
 */
export function AdminOnlyGuardProvider({ resetKey, children }: { resetKey: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setVisible(false); }, [resetKey]);
  const show = useCallback(() => setVisible(true), []);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <GuardContext.Provider value={value}>
      {visible && <p role="alert" data-testid="admin-only-message" className="mb-4 text-sm font-medium text-red-600">{ADMIN_ONLY_MESSAGE}</p>}
      {children}
    </GuardContext.Provider>
  );
}

/** `const canApply = useAdminGuard(); ... if (!canApply()) return;` -- false for anybody who is not an admin, after showing the red message. */
export function useAdminGuard(): () => boolean {
  const { userRole } = useUser();
  const ctx = useContext(GuardContext);
  return useCallback(() => {
    if (userRole === "admin") return true;
    ctx?.show();
    return false;
  }, [userRole, ctx]);
}
